import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface MaintenanceWindowDto {
  id: number;
  title: string;
  description: string | null;
  startDateTimeUtc: string;
  endDateTimeUtc: string;
  isActive: boolean;
  targetApplication: number;
  createdDate: string;
  updatedDate: string | null;
}

interface MaintenanceCheckResponse {
  success: boolean;
  errors: string[];
  data: MaintenanceWindowDto | null;
}

// Cap setTimeout at 24 hours to avoid JS int32 overflow (max ~24.8 days)
const MAX_TIMEOUT_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class MaintenanceCheckService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiOrigin + '/users/api';
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly isUnderMaintenance = signal(false);
  readonly maintenanceEndTime = signal<Date | null>(null);

  checkMaintenance(): Observable<MaintenanceWindowDto | null> {
    const url = `${this.baseUrl}/maintenancewindow/active?application=2`;

    return this.http.get<MaintenanceCheckResponse>(url).pipe(
      map(response => response.success ? response.data : null),
      catchError(() => of(null))
    );
  }

  /**
   * Called by APP_INITIALIZER to block bootstrap until maintenance status is known.
   * Sets up auto-clear timer with safe timeout capping.
   */
  initializeCheck(): Observable<MaintenanceWindowDto | null> {
    return this.checkMaintenance().pipe(
      tap(maintenance => this.applyMaintenanceState(maintenance))
    );
  }

  private applyMaintenanceState(maintenance: MaintenanceWindowDto | null): void {
    this.clearTimer();

    if (!maintenance) {
      this.isUnderMaintenance.set(false);
      this.maintenanceEndTime.set(null);
      return;
    }

    const endUtc = maintenance.endDateTimeUtc.endsWith('Z')
      ? maintenance.endDateTimeUtc
      : maintenance.endDateTimeUtc + 'Z';
    const endDate = new Date(endUtc);

    if (isNaN(endDate.getTime())) {
      this.isUnderMaintenance.set(true);
      return;
    }

    const msUntilEnd = endDate.getTime() - Date.now();
    if (msUntilEnd <= 0) return; // window already expired

    this.isUnderMaintenance.set(true);
    this.maintenanceEndTime.set(endDate);
    this.scheduleEndCheck(endDate);
  }

  /**
   * Schedules maintenance end with safe timeout capping.
   * If msUntilEnd exceeds MAX_TIMEOUT_MS (24h), re-checks periodically.
   */
  private scheduleEndCheck(endDate: Date): void {
    const msUntilEnd = endDate.getTime() - Date.now();

    if (msUntilEnd <= 0) {
      this.isUnderMaintenance.set(false);
      this.maintenanceEndTime.set(null);
      return;
    }

    const delay = Math.min(msUntilEnd, MAX_TIMEOUT_MS);
    this.timeoutId = setTimeout(() => {
      if (endDate.getTime() <= Date.now()) {
        this.isUnderMaintenance.set(false);
        this.maintenanceEndTime.set(null);
      } else {
        this.scheduleEndCheck(endDate);
      }
    }, delay);
  }

  private clearTimer(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
